import { StoreService } from 'projects/dop-core/src/public-api';
import { Operations } from 'src/app/core/guards/enum/operations';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { EolctDataService } from 'src/app/shared/data-service/eolct-data.service';
import { LabConfigurationService } from 'src/app/shared/data-service/lab-configuration.service';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { UserManagementDataService } from 'src/app/shared/data-service/user-management-data.service';
import { LabType } from 'src/app/shared/enums/lab-type.enum';
import { OrderStateChangeEnum } from 'src/app/shared/enums/order-state-change.model';
import { OrderState } from 'src/app/shared/enums/order-state.enum';
import { UserType } from 'src/app/shared/enums/user-type.enum';
import { CustomerAccountsRequest } from 'src/app/shared/models/customer-accounts-request.model';
import { LabModel } from 'src/app/shared/models/lab.model';
import { AccountRequest } from 'src/app/shared/models/order-creation/account-request.model';
import { QuotationContractRequest } from 'src/app/shared/models/order-creation/quotation-contract-request.model';
import { OrderTemplate } from 'src/app/shared/models/order-template.model';
import { QuotationMessage } from 'src/app/shared/models/quotation-message.model';
import { SearchAccountsRequest } from 'src/app/shared/models/search-accounts-request.model';
import { UserDetails } from 'src/app/shared/models/user-details.model';
import { OrderDetailsCleanUpService } from 'src/app/shared/order-cleanup/order-details-cleanup.service';
import { Injectable } from '@angular/core';
import { OrderSetupContext } from '../../shared/models/order-creation/order-setup.context';
import { CreateOrderService } from 'src/app/shared/order-validator/create-order.service';
import { CurrentOrderService } from 'src/app/shared/services/current-order.service';
import { AccountsOrderingServiceResponse } from 'src/app/shared/models/order-creation/accounts-ordering-service-response.model';
import { SampleResponse, SampleDetailsResponse } from 'src/app/shared/models/sample-response.model';
import { BulkSampleRequest } from 'src/app/shared/models/bulk-sample-creation/bulk-sample-request.model';
import { SampleDetails } from 'src/app/shared/models/bulk-sample-creation/sample-details.model';
import { TranslationService } from 'projects/dop-core/src/public-api';
import { SAMPLE } from '../guided-order/samples/constants/sample-constants';
import { catchError, map } from 'rxjs/operators';
import { CommercialContract } from 'src/app/shared/models/commercial-contract.model';
import { InvoiceableItemRequest } from 'src/app/shared/models/order-creation/invoiceable-item-request.model';
import { QuotationLine } from 'src/app/shared/models/order-creation/quotation-line.model';
import { CommercialItem } from 'src/app/shared/models/commercial-item.model';
import { SampleInvoiceableItemsResponse } from 'src/app/shared/models/sample-invoiceable-items-response.model';
import { SpecificationRequest, SpecificationUnit } from 'src/app/shared/models/specification/specification-request.model';
import { SpecificationType } from 'src/app/shared/models/specification/specification-type.model';
import { HelperService } from 'src/app/shared/services/helper.service';
import { SettingsService } from 'src/app/shared/data-service/settings.service';
import { SettingsKey } from 'src/app/core/store/settings-config.keys';
import { LabsiteCheckService } from 'src/app/shared/labsite-check/labsite-check.service';

import { IntercoEleAccountDetails } from 'src/app/shared/models/ele-account.model';

@Injectable({
  providedIn: 'root'
})
export class OrderSetupService {

  private orderSetupContext: OrderSetupContext;

  constructor(
    private userManagementDataService: UserManagementDataService,
    private storeService: StoreService,
    private eolCtDataService: EolctDataService,
    private labConfigurationService: LabConfigurationService,
    private orderDataService: OrderDataService,
    private orderCommandService: OrderCommandService,
    private orderDetailsCleanUpService: OrderDetailsCleanUpService,
    private createOrderService: CreateOrderService,
    private currentOrderService: CurrentOrderService,
    private helperService: HelperService,
    private settingService: SettingsService,
    private labsiteCheck: LabsiteCheckService) { }

  async loadAccounts(searchText?: string, pageNo?: number, pageSize?: number): Promise<number> {
    return new Promise(async (resolve) => {
      const userDetails = await this.storeService.Get<UserDetails>(StoreKey[StoreKey.UserDetails]).toPromise();
      const userPermission = await this.storeService.Get<any>(StoreKey[StoreKey.UserPermission]).toPromise();
      if (userDetails.profile.userType.toString().toLowerCase() !== UserType[0].toString().toLowerCase()) {
        this.userManagementDataService.getInternalUserAccounts(this.getSearchObject(searchText, userPermission, pageNo, pageSize))
          .subscribe(async (data) => {
            const intercoEleAccountDetails = await this.storeService.Get<IntercoEleAccountDetails[]>(StoreKey[StoreKey.IntercoEleAccountDetails]).toPromise();
            const intercoAccounts = this.searchInIntercoEleAccountDetails(intercoEleAccountDetails, searchText);
            
            // Merge API results with interco accounts, removing duplicates
            const mergedAccounts = this.mergeAndDeduplicateAccounts(data.accounts || [], intercoAccounts);
            
            if (pageNo > 1) {
              // append the paginated data
              this.orderSetupContext.masterData?.accounts?.push(...mergedAccounts);
              this.setDefaultAccount(mergedAccounts, userDetails);
            } else {
              this.orderSetupContext.masterData.accounts = mergedAccounts;
              this.setDefaultAccount(mergedAccounts, userDetails);
            }
            resolve(data.totalRecords + intercoAccounts.length);
          });
      } else {
        const accountRequest: CustomerAccountsRequest = new CustomerAccountsRequest();
        const accountCodesHavingPermission
          = userPermission.filter((x: any) =>
            x.operations.indexOf(Operations.CanManageGuidedOrdering) > -1);
        accountRequest.accountCodes = accountCodesHavingPermission.map((x: any) => x.accountCode);

        this.userManagementDataService.getCustomerUserAccounts(accountRequest).subscribe((accounts: any) => {
          accounts = accounts.filter((x: any) => x.isActive);
          if (userDetails.profile.defaultAccountCode) {
            this.orderSetupContext.transactionalData.account =
              accounts.filter((account: any) => account.code === userDetails.profile.defaultAccountCode)[0];
          }
          this.orderSetupContext.masterData.accounts = accounts;
          this.setDefaultAccount(accounts, userDetails);
          resolve(accounts?.length);
        });
      }
    });
  }

  async loadOrderTemplate(accountCode: string) {
    return new Promise(async (resolve) => {
      this.orderDataService.getOrderTemplatesForAccounts([accountCode], false)
        .subscribe((data: AccountsOrderingServiceResponse[]) => {
          this.orderSetupContext.masterData.orderTemplates = data[0]?.orderingServices;
          this.setDefalutOS();
          resolve(null);
        }, () => {
          this.orderSetupContext.masterData.orderTemplates = [];
        });
    });
  }

  async getLab(receivingLabCode: string): Promise<LabModel> {
    return new Promise(async (resolve) => {
      this.orderDataService.getLabType(receivingLabCode)
        .subscribe((data: LabModel) => {
          resolve(data);
        });
    });
  }

  async getLabType(receivingLabCode: string): Promise<string> {
    return new Promise(async (resolve) => {
      this.orderDataService.getLabType(receivingLabCode)
        .subscribe((data: any) => {
          resolve(data.type);
        });
    });
  }

  async loadQuotations(accountCode: string) {
    return this.orderDataService.getQuotationsForAccount(accountCode, false)
      .pipe(map((response: QuotationMessage) => {
        if (response.quotations.length > 0) {
          this.labConfigurationService.LabCode = response.quotations[0].receivingLabCode;
        }
        this.orderSetupContext.masterData.quotations = response.quotations;
        return this.setDefaultQuotation();
      }), catchError(() => []))
      .toPromise();
  }

  async loadContract(accountCode: string, quotationCode: any): Promise<CommercialContract[]> {
    return this.orderDataService.getContractsByAccountAndQuotation(accountCode, quotationCode)
      .pipe(map((response: any) => {
        this.orderSetupContext.masterData.contracts = response ?? [];
        this.setDefaultContract();
        return this.orderSetupContext.masterData.contracts;
      }),
        catchError(() => this.orderSetupContext.masterData.contracts = []))
      .toPromise();
  }

  async loadExternalLimsAccounts(accountCode: string): Promise<CommercialContract[]> {
    return this.orderDataService.getExternalLimsContractsByAccount(accountCode)
      .pipe(map((response: any) => {
        this.orderSetupContext.masterData.contracts = response ?? [];
        return this.orderSetupContext.masterData.contracts;
      }),
        catchError(() => this.orderSetupContext.masterData.contracts = []))
      .toPromise();
  }

  handleDefaultQuotationSelection() {
    if (!this.orderSetupContext.transactionalData?.quotation.code) {
      const defQuotation = this.orderSetupContext.masterData?.quotations
        .filter(c => c.code === this.orderSetupContext.transactionalData?.orderTemplate.defaultQuotation?.code).shift();
      if (defQuotation) {
        this.orderSetupContext.transactionalData.quotation = defQuotation;
      }
    }
  }

  async selectAllSingleFields() {
    let isSingle = false;
    const userDetails = await this.storeService.Get<UserDetails>(StoreKey[StoreKey.UserDetails]).toPromise();
    if (userDetails.profile.userType.toString().toLowerCase() === UserType[0].toString().toLowerCase()) {
      await this.loadAccounts();
      if (this.orderSetupContext.masterData?.accounts.length === 1) {
        this.orderSetupContext.transactionalData.account = this.orderSetupContext.masterData?.accounts[0];
        await this.loadOrderTemplate(this.orderSetupContext.transactionalData?.account.code);
        await this.loadQuotations(this.orderSetupContext.transactionalData?.account.code);
        if (this.orderSetupContext.masterData?.orderTemplates.length === 1
          && this.orderSetupContext.masterData?.quotations.length === 1) {
          this.orderSetupContext.transactionalData.orderTemplate = this.orderSetupContext.masterData?.orderTemplates[0];
          this.orderSetupContext.transactionalData.quotation = this.orderSetupContext.masterData?.quotations[0];
          const labType = await this.getLabType(this.orderSetupContext.transactionalData?.quotation.receivingLabCode);
          if (await this.labsiteCheck.isElimsOrTransitionedToElims(this.orderSetupContext.transactionalData?.quotation.receivingLabCode,
            labType, this.orderSetupContext.transactionalData?.account.code)) {
            await this.loadContract(this.orderSetupContext.transactionalData?.account.code,
              this.orderSetupContext.transactionalData?.quotation.code);
            if (this.orderSetupContext.masterData?.contracts.length === 1) {
              this.orderSetupContext.transactionalData.contract = this.orderSetupContext.masterData?.contracts[0];
              isSingle = true;
              return true;
            }
          }
        }
      }
    }
    return isSingle;
  }

  handleDefaultContractSelection() {
    if (!this.orderSetupContext.transactionalData?.contract.code) {
      const defContract = this.orderSetupContext.masterData?.contracts
        .filter(c => c.code === this.orderSetupContext.transactionalData?.orderTemplate.defaultContract?.code).shift();
      if (defContract) {
        this.orderSetupContext.transactionalData.contract = defContract;
      }
    }
  }

  resetContextOnAccountChange() {
    this.orderSetupContext.transactionalData.orderTemplate = {};
    this.orderSetupContext.transactionalData.quotation = {};
    this.orderSetupContext.transactionalData.contract = {};
    this.orderSetupContext.masterData.orderTemplates = undefined;
    this.orderSetupContext.masterData.quotations = undefined;
    this.orderSetupContext.masterData.contracts = undefined;

  }

  resetContext() {
    this.orderSetupContext = {
      masterData: {
        accounts: [],
      },
      transactionalData: {
        account: {},
        orderTemplate: {},
        quotation: {},
        OrderName: '',
        contract: {},
        stateChange: OrderStateChangeEnum.NoChange,
        orderState: OrderState.New
      }
    };
  }

  getContext(): OrderSetupContext {
    if (!this.orderSetupContext) {
      this.resetContext();
    }

    return this.orderSetupContext;
  }

  saveOrderingTemplateInStore(orderTemplate: OrderTemplate) {
    this.storeService.Save(StoreKey.SelectedOrderTemplate, orderTemplate, true);
  }

  setContext(orderSetupContext: OrderSetupContext) {
    this.orderSetupContext = orderSetupContext;
  }

  private getSearchObject(searchText: any, eleCodes: any, pageNumber: any, pageSize: any): SearchAccountsRequest {
    return {
      sortColumn: 'AccountCode',
      sortDirection: 0,
      pageNumber,
      recordsCount: pageSize,
      searchText,
      pageSize,
      eleCodes
    };
  }

  async updateOrderDetails() {
    await this.updateOrderAccount();
    await this.updateQuotationContract();
  }

  async updateOrderAccount() {
    const accountRequest: AccountRequest = {
      accountCode: this.orderSetupContext.transactionalData?.account.code,
      accountName: this.orderSetupContext.transactionalData?.account.name,
      orderingServiceCode: this.orderSetupContext.transactionalData?.orderTemplate.code,
      orderingServiceName: this.orderSetupContext.transactionalData?.orderTemplate.name
    };
    await this.orderCommandService.updateOrderAccount(this.orderSetupContext.transactionalData?.OrderCode,
      accountRequest).toPromise();
  }

  validateOrderNameChangeAndSave() {
    if (this.orderSetupContext.transactionalData?.OrderName) {
      this.orderSetupContext.transactionalData.OrderName = this.orderSetupContext.transactionalData?.OrderName.trim();
    }

    if (this.orderSetupContext.transactionalData?.orderState === OrderState.Pending ||
      this.orderSetupContext.transactionalData?.orderState === OrderState.EditFavoriteOrder) {
      if (this.orderSetupContext.transactionalData?.OrderName !== this.orderSetupContext.transactionalData?.oldOrderName?.trim()) {
        this.orderCommandService.patchOrderDetails(this.orderSetupContext.transactionalData?.OrderCode,
          [{ name: 'name', value: this.orderSetupContext.transactionalData?.OrderName }]).toPromise();
      }
    }
  }

  async updateQuotationContract() {
    const labType = await this.getLabType(this.orderSetupContext.transactionalData?.quotation.receivingLabCode);
    this.orderSetupContext.transactionalData.isElimslab = await this.labsiteCheck.isElimsOrTransitionedToElims(this.orderSetupContext.transactionalData?.quotation.receivingLabCode,
      labType, this.orderSetupContext.transactionalData?.account.code);
    const quotationContractRequest: QuotationContractRequest = {
      quotation: {
        code: this.orderSetupContext.transactionalData?.quotation.code,
        name: this.orderSetupContext.transactionalData?.quotation.name
      },
      contract: {
        ExternalLIMSContractCode: this.orderSetupContext.transactionalData?.isElimslab
          ? null : this.orderSetupContext.transactionalData?.contract.code,
        ExternalLIMSContractName: this.orderSetupContext.transactionalData?.isElimslab
          ? null : this.orderSetupContext.transactionalData?.contract.name,
        code: this.orderSetupContext.transactionalData?.isElimslab
          ? this.orderSetupContext.transactionalData?.contract.code : null,
        name: this.orderSetupContext.transactionalData?.isElimslab
          ? this.orderSetupContext.transactionalData?.contract.name : null
      },
      receivingPartnerCode: this.orderSetupContext.transactionalData?.quotation.receivingLabCode,
      salesPartnerCode: this.orderSetupContext.transactionalData?.quotation.salesPartnerCode,
      receivingLabName: this.orderSetupContext.transactionalData?.quotation.receivingLabName,
      salesLabName: this.orderSetupContext.transactionalData?.quotation.salesPartnerName
    };

    if (this.orderSetupContext.transactionalData?.orderState !== OrderState.New) {
      await this.orderCommandService
        .updateOrderQuotationContract(this.orderSetupContext.transactionalData?.OrderCode,
          quotationContractRequest).toPromise();
    }
  }

  async validateNewLabs() {
    if (this.orderSetupContext.transactionalData?.oldReceivingPartnerCode
      && this.orderSetupContext.transactionalData?.oldReceivingPartnerCode
      !== this.orderSetupContext.transactionalData?.quotation.receivingLabCode) {
      if (this.orderSetupContext.transactionalData?.orderState === OrderState.Pending ||
        this.orderSetupContext.transactionalData?.orderState === OrderState.EditFavoriteOrder) {
        this.deleteAllSamples();
        this.orderSetupContext.transactionalData.orderState = OrderState.New;
      } else {
        this.orderSetupContext.transactionalData.orderState = OrderState.New;
        this.orderSetupContext.transactionalData.OrderCode = null;
      }
    } else if (this.orderSetupContext.transactionalData?.oldReceivingPartnerCode
      === this.orderSetupContext.transactionalData?.quotation.receivingLabCode) {
      (this.orderSetupContext.transactionalData?.orderState === OrderState.Pending ||
        this.orderSetupContext.transactionalData?.orderState === OrderState.EditFavoriteOrder)
        ? this.deleteAllAnalyses()
        : await this.createOrderWithSamples(false);
    }
  }

  deleteAllAnalyses() {
    this.currentOrderService.currentOrder.samples = [];
    this.orderDetailsCleanUpService.deleteAllAnalyses(this.getContext().transactionalData?.sampleCodes);
  }

  async deleteAllSamples() {
    await this.orderDetailsCleanUpService.deleteAllSamples(this.orderSetupContext.transactionalData?.OrderCode);
  }

  async createOrderWithSamples(createWithAnalysis = false) {
    await this.createOrderService.createNewOrder(this.orderSetupContext.transactionalData,
      this.orderSetupContext.transactionalData?.eleCode,
      this.orderSetupContext.transactionalData);

    if (this.orderSetupContext.transactionalData?.OrderReference === undefined) {
      return;
    }
    // Get Samples of submittedOrder/ Fav Order
    const samples = await this.orderDataService.getSamples(this.orderSetupContext.transactionalData?.oldOrderCode);
    samples.map(x => x.isExisting = true);
    let invoiceableItems: SampleInvoiceableItemsResponse[] = [];

    // Get previous sample analysis only if needed to be created for new Order
    if (createWithAnalysis) {
      invoiceableItems = await this.orderDataService.getSampleInvoiceableItems(samples.map(x => x.sampleDetails.id)).toPromise();
    }
    // Create these samples for new Order
    const bulkSamples = this.convertSampleResponseToSampleRequest(samples, invoiceableItems);

    await this.orderCommandService.addSamplesForOrder(this.orderSetupContext.transactionalData?.OrderCode,
      bulkSamples).toPromise().catch(error => {
        this.orderSetupContext.transactionalData.sampleGlcError = true;
        const errorMessage = this.helperService.handleHttpError(error, SAMPLE, true);
        this.orderCommandService.showError({ detail: TranslationService.translate(errorMessage) });
      });
    this.orderSetupContext.transactionalData.orderState = OrderState.Pending;
  }

  setCurrentOrderContext(orderSetupContext: OrderSetupContext, isSubmitOrder = false) {
    // Need to set each property as in case of submitted orders flow sample details will already be filled
    if (!this.currentOrderService.currentOrder) {
      this.currentOrderService.currentOrder = {};
    }
    this.currentOrderService.currentOrder.account = { ...orderSetupContext.transactionalData?.account };
    this.currentOrderService.currentOrder.contract = { ...orderSetupContext.transactionalData?.contract };
    this.currentOrderService.currentOrder.quotation = { ...orderSetupContext.transactionalData?.quotation };
    this.currentOrderService.currentOrder.OrderName = orderSetupContext.transactionalData?.OrderName;
    this.currentOrderService.currentOrder.orderTemplate = { ...orderSetupContext.transactionalData?.orderTemplate };
    this.currentOrderService.currentOrder.orderState = orderSetupContext.transactionalData?.orderState;
    this.currentOrderService.currentOrder.OrderReference = orderSetupContext.transactionalData?.OrderReference;
    this.currentOrderService.currentOrder.OrderCode = orderSetupContext.transactionalData?.OrderCode;
    this.currentOrderService.currentOrder.replicatedFromOrderReference = isSubmitOrder
      ? orderSetupContext.transactionalData?.OrderReference
      : orderSetupContext.transactionalData?.replicatedFromOrderReference;
    this.currentOrderService.currentOrder.oldOrderCode = isSubmitOrder
      ? orderSetupContext.transactionalData?.OrderCode
      : orderSetupContext.transactionalData?.oldOrderCode;
    this.currentOrderService.currentOrder.CustomerComment = this.orderSetupContext.transactionalData?.customerComment;
    this.currentOrderService.currentOrder.CustomerReference = this.orderSetupContext.transactionalData?.customerReference;
    this.currentOrderService.currentOrder.additionalFields = this.orderSetupContext.transactionalData?.additionalFields;
    this.currentOrderService.currentOrder.recipients = this.orderSetupContext.transactionalData?.recipients;
    this.currentOrderService.currentOrder.CreatedBy = this.orderSetupContext.transactionalData?.createdBy;
    this.currentOrderService.currentOrder.CreatedOn = this.orderSetupContext.transactionalData?.createdOn;
    this.currentOrderService.currentOrder.standardCommentTemplate = this.orderSetupContext.transactionalData?.standardCommentTemplate;
    this.currentOrderService.currentOrder.productMatrices = this.orderSetupContext.transactionalData?.productMatrices;
  }

  setEleInterEleCode(orderSetupContext: OrderSetupContext) {
    orderSetupContext.transactionalData.eleCode = orderSetupContext.transactionalData?.quotation.legalEntityCode;
    orderSetupContext.transactionalData.intercoEleCode = orderSetupContext.transactionalData?.account.intercoEurofinsLegalEntityCode;
  }

  private convertSampleResponseToSampleRequest(
    sampleResponse: SampleResponse[],
    invoiceableItems: SampleInvoiceableItemsResponse[]): BulkSampleRequest[] {
    const bulkSampleRequest: BulkSampleRequest[] = [];
    sampleResponse?.forEach(sample => {
      const bulkSample: BulkSampleRequest = {
        sampleDetails: this.convertSampleDetails(sample.sampleDetails),
        additionalFields: sample.sampleAdditionalFields,
        invoiceableItem:
          this.prepareSampleInvoiceableItemRequest(this.orderSetupContext.transactionalData?.quotation.code,
            invoiceableItems.filter(x => x.sampleId === +sample.sampleDetails.id).shift()),
        specifications: this.createSpecificationRequest(invoiceableItems.filter(x => x.sampleId === +sample.sampleDetails.id).shift())
      };
      bulkSampleRequest.push(bulkSample);
    });
    return bulkSampleRequest;
  }

  private convertSampleDetails(sampleDetailsResponse: SampleDetailsResponse): SampleDetails {
    const sampleDetails: SampleDetails = {
      customerReference: sampleDetailsResponse.code,
      description: sampleDetailsResponse.description,
      comment: sampleDetailsResponse.comment,
      lotNumber: sampleDetailsResponse.lotNumber,
      templateCode: sampleDetailsResponse.sampleTemplateCode,
      sampleReference: sampleDetailsResponse.sampleReference,
      analyticalReportGroupingRule: sampleDetailsResponse.analyticalReportGroupingRule,
      priorityLevel: sampleDetailsResponse.priorityLevel,
      resultTransformation: sampleDetailsResponse.resultTransformation,
      recommendationPublication: sampleDetailsResponse.recommendationPublication,
      servingUnitNumeratorName: sampleDetailsResponse.servingUnitName,
      servingUnitNumeratorCode: sampleDetailsResponse.servingUnitCode,
      servingSize: +sampleDetailsResponse.servingSize,
      productCode: sampleDetailsResponse.productCode,
      groupingCode: sampleDetailsResponse.groupId
    };

    return sampleDetails;
  }

  private prepareSampleInvoiceableItemRequest(
    quotationCode: string, invoiceableItems: SampleInvoiceableItemsResponse): InvoiceableItemRequest {
    const requestModel = new InvoiceableItemRequest();
    invoiceableItems?.sampleInvoiceableItemList?.forEach(x => {
      if (this.validateIfNullOrEmpty(x.salesCatagloueCode)) {
        const commercialItem = new CommercialItem();
        commercialItem.invoiceableItemParentCode = x.salesCatagloueCode;
        if (this.validateIfNullOrEmpty(x.packageCode)) {
          requestModel.package = requestModel.package ?? [];
          if (requestModel.package.every(y => y.code !== x.packageCode
            && y.invoiceableItemParentCode !== x.salesCatagloueCode
            && y.monographCode !== x.monographCode)) {
            commercialItem.code = x.packageCode;
            commercialItem.monographCode = x.monographCode;
            commercialItem.compendiumCode = x.compendiumCode;
            requestModel.package.push(commercialItem);
          }
        } else if (this.validateIfNullOrEmpty(x.testCode)) {
          requestModel.test = requestModel.test ?? [];
          if (requestModel.test.every(y => y.code !== x.testCode && y.invoiceableItemParentCode !== x.salesCatagloueCode
            && y.monographCode !== x.monographCode)
            || requestModel.test.every(y => y.code !== x.testBundleCode && y.invoiceableItemParentCode !== x.salesCatagloueCode
              && y.monographCode !== x.monographCode)) {
            commercialItem.code = x.testCode;
            commercialItem.monographCode = x.monographCode;
            commercialItem.compendiumCode = x.compendiumCode;
            commercialItem.testBundleCode = x.testBundleCode;
            requestModel.test.push(commercialItem);
          }
        }
      } else {
        const quotationLine = new QuotationLine();
        quotationLine.invoiceableItemParentCode = quotationCode;
        if (this.validateIfNullOrEmpty(x.customerPackageCode)) {
          requestModel.customerPackage = requestModel.customerPackage ?? [];
          if (requestModel.customerPackage.every(y => y.code !== x.customerPackageCode)) {
            quotationLine.code = x.customerPackageCode;
            quotationLine.comment = x.comment;
            requestModel.customerPackage.push(quotationLine);
          }
        } else if (this.validateIfNullOrEmpty(x.packageCode)) {
          requestModel.packageQuotationLine = requestModel.packageQuotationLine ?? [];
          if (requestModel.packageQuotationLine.every(y => y.code !== x.packageCode && y.subGroupCode !== x.subGroupCode)) {
            quotationLine.code = x.quotationLineCode;
            quotationLine.comment = x.comment;
            quotationLine.subGroupCode = x.subGroupCode;
            requestModel.packageQuotationLine.push(quotationLine);
          }
        } else if (this.validateIfNullOrEmpty(x.testCode)) {
          quotationLine.code = x.quotationLineCode;
          quotationLine.comment = x.comment;
          quotationLine.subGroupCode = x.subGroupCode;
          requestModel.testQuotationLine = requestModel.testQuotationLine ?? [];
          requestModel.testQuotationLine.push(quotationLine);
        }
      }
    });

    return requestModel;
  }

  private createSpecificationRequest(invoiceableItems: SampleInvoiceableItemsResponse): SpecificationRequest[] {
    const requestObject: SpecificationRequest[] = [];
    invoiceableItems?.sampleInvoiceableItemList?.forEach(analysis => {
      analysis.specifications?.forEach(spec => {
        const requestParam = requestObject.filter(x => x.parameterCode === spec.parameterCode).shift();
        if (!requestParam) {
          const newSpec = new SpecificationRequest();
          newSpec.parameterCode = spec.parameterCode;
          newSpec.parameterName = spec.parameterName;
          newSpec.specificationUnit = this.buildSpecificationUnit(spec.numeratorUnitCode, spec.numeratorUnitName,
            spec.denominatorUnitCode, spec.denominatorUnitName),
            newSpec.expectedValue = spec.expectedValue;
          newSpec.lowerLimit = spec.lowerLimit;
          newSpec.upperLimit = spec.upperLimit;
          newSpec.specificationTypeId = this.getSpecificationType(spec.specificationType);
          newSpec.testCodes = [analysis.testCode];
          requestObject.push(newSpec);
        } else {
          requestParam.testCodes.push(analysis.testCode);
        }
      });

    });

    return requestObject;
  }

  private buildSpecificationUnit(
    numeratorCode: string, numeratorName: string,
    denominatorCode: string, denominatorName: string): SpecificationUnit {
    return {
      numeratorUnitCode: numeratorCode,
      numeratorUnitName: numeratorName,
      denominatorUnitCode: denominatorCode,
      denominatorUnitName: denominatorName
    };
  }

  private getSpecificationType(specificationType: string): SpecificationType {
    switch (specificationType) {
      case 'Standard':
        return SpecificationType.Standard;
      case 'ServingPortion':
        return SpecificationType.ServingPortion;
      case 'DailyValue':
        return SpecificationType.DailyValue;
      case 'LabelClaim':
        return SpecificationType.LabelClaim;
    }
  }

  private validateIfNullOrEmpty(value: string): boolean {
    return value && value.trim() !== '';
  }

  private setDefaultAccount(accounts: any, userDetails: UserDetails) {
    accounts.forEach((d: { code: string; }) => {
      this.orderSetupContext.masterData?.accounts?.forEach(m => {
        if (userDetails.profile.defaultAccountCode === m.code) {
          m.isDefaultAccount = true;
        }
      });
    });
  }

  private setDefalutOS() {
    const deafaultOSCode = this.settingService.getConfigValue(SettingsKey.DefaultOrderingService);
    this.orderSetupContext.masterData?.orderTemplates?.forEach(x => {
      if (x.code === deafaultOSCode) {
        x.isDefaultOrderingService = true;
      }
    });
  }

  private setDefaultQuotation(): void {
    return this.orderSetupContext.masterData?.quotations?.forEach(x => {
      this.orderSetupContext.masterData?.orderTemplates?.forEach(y => {
        if (y.defaultQuotation?.code === x.code) {
          x.isDefaultQuotation = true;
        }
      });
    });
  }

  private setDefaultContract() {
    this.orderSetupContext.masterData?.contracts?.forEach(x => {
      this.orderSetupContext.masterData?.orderTemplates?.forEach(y => {
        if (y.defaultContract?.code === x.code) {
          x.isDefaultContract = true;
        }
      });
    });
  }

  private searchInIntercoEleAccountDetails(intercoEleAccountDetails: IntercoEleAccountDetails[], searchText: string): any[] {
    if (!intercoEleAccountDetails || !searchText || searchText.length < 3) {
      return [];
    }

    const searchLower = searchText.toLowerCase();
    const matchingIntercoAccounts: any[] = [];

    intercoEleAccountDetails.forEach(intercoEleDetail => {
      intercoEleDetail.accounts?.forEach(account => {
        // Search by account code, name, or city
        const matchesCode = account.code?.toLowerCase().includes(searchLower);
        const matchesName = account.name?.toLowerCase().includes(searchLower);
        const matchesCity = account.address?.city?.toLowerCase().includes(searchLower);

        if (matchesCode || matchesName || matchesCity) {
          matchingIntercoAccounts.push(account);
        }
      });
    });

    return matchingIntercoAccounts;
  }

  private mergeAndDeduplicateAccounts(apiAccounts: any[], intercoAccounts: any[]): any[] {
    const accountMap = new Map<string, any>();

    // Add API accounts first
    apiAccounts.forEach(account => {
      if (account.code) {
        accountMap.set(account.code, account);
      }
    });

    // Add interco accounts (won't overwrite if already exists from API)
    intercoAccounts.forEach(account => {
      if (account.code && !accountMap.has(account.code)) {
        accountMap.set(account.code, account);
      }
    });

    return Array.from(accountMap.values());
  }
}
